import type {
  AgentSelection,
  ApprovalDecision,
  ApprovalRecord,
  ArtifactRecord,
  AttachmentRecord,
  BootstrapPayload,
  CapabilityInventory,
  CapabilityApprovalMode,
  CodexResetPolicy,
  CodexResetResult,
  ConfigurationPayload,
  DriverId,
  FileBrowserDirectory,
  FileBrowserEntry,
  FileTextPreview,
  InteractionAnswerInput,
  MemoryKind,
  MemoryHygieneFinding,
  MemoryRecord,
  MemoryScope,
  MemoryStatus,
  NoraSettings,
  NoraPersonality,
  ProjectBlockerRecord,
  ProjectAutonomyMode,
  ProjectDetail,
  ProjectGoalRecord,
  ProjectGoalStatus,
  ProjectProgressStatus,
  ProjectSummary,
  ProjectTrackingMode,
  QuickPromptContext,
  QuickPromptRecord,
  RecurringTaskDetail,
  RecurringTaskRecord,
  RecurringTaskRunRecord,
  RecurringTaskSchedule,
  SessionDetail,
  SessionSummary,
  StatusPayload,
  ThemeId,
  TodayDashboard,
  UniversalSearchResponse,
} from "./types";

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

export async function getAuthStatus(): Promise<boolean> {
  const response = await fetch("/api/auth/status", { credentials: "same-origin" });
  if (!response.ok) return false;
  return Boolean((await response.json()).authenticated);
}

export async function getRecoveryToken(): Promise<string | null> {
  const response = await fetch("/api/auth/recovery-token", {
    credentials: "same-origin",
    cache: "no-store",
  });
  if (response.status === 204) return null;
  if (!response.ok) return null;
  const payload = await response.json() as { token?: unknown };
  return typeof payload.token === "string" ? payload.token : null;
}

export async function login(token: string): Promise<void> {
  await apiFetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
}

export async function logout(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" });
}

export async function getBootstrap(): Promise<BootstrapPayload> {
  const payload = await apiJson<BootstrapPayload>("/api/bootstrap");
  if (payload.sessions.every((session) => Number.isFinite(session.pendingApprovalCount))) {
    return payload;
  }
  const approvals = await getPendingApprovals();
  const counts = new Map<string, number>();
  for (const approval of approvals) {
    counts.set(approval.sessionId, (counts.get(approval.sessionId) ?? 0) + 1);
  }
  return {
    ...payload,
    sessions: payload.sessions.map((session) => ({
      ...session,
      pendingApprovalCount: counts.get(session.id) ?? 0,
    })),
  };
}

export function getStatus(): Promise<StatusPayload> {
  return apiJson("/api/status");
}

export function updateCodexResetPolicy(
  enabled: boolean,
  thresholdPercent: number
): Promise<{ resetPolicy: CodexResetPolicy; automaticResult: CodexResetResult | null }> {
  return apiJson("/api/quotas/reset-policy", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ enabled, thresholdPercent }),
  });
}

export function consumeCodexReset(): Promise<CodexResetResult> {
  return apiJson("/api/quotas/reset", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ confirmed: true }),
  });
}

export function getToday(signal?: AbortSignal): Promise<TodayDashboard> {
  return apiJson("/api/today", signal ? { signal } : undefined);
}

export async function resolveApproval(
  approvalId: string,
  decision: ApprovalDecision
): Promise<ApprovalRecord> {
  const payload = await apiJson<{ approval: ApprovalRecord }>(
    `/api/approvals/${encodeURIComponent(approvalId)}/resolve`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ decision }),
    }
  );
  return payload.approval;
}

async function getPendingApprovals(): Promise<ApprovalRecord[]> {
  const payload = await apiJson<{ approvals: ApprovalRecord[] }>("/api/approvals?status=pending");
  return payload.approvals;
}

export function searchNora(query: string, signal?: AbortSignal): Promise<UniversalSearchResponse> {
  const params = new URLSearchParams({ q: query });
  return apiJson(`/api/search?${params.toString()}`, signal ? { signal } : undefined);
}

export function reindexSearch(): Promise<{ indexed: number; truncated: boolean; inProgress: boolean }> {
  return apiJson("/api/search/reindex", { method: "POST" });
}

export function getArtifacts(filters: {
  projectId?: string | null;
  status?: "active" | "archived" | "all";
  query?: string;
} = {}, signal?: AbortSignal): Promise<{ artifacts: ArtifactRecord[]; total: number }> {
  const query = new URLSearchParams();
  if (filters.projectId) query.set("projectId", filters.projectId);
  if (filters.status) query.set("status", filters.status);
  if (filters.query) query.set("q", filters.query);
  return apiJson(
    `/api/artifacts${query.size ? `?${query.toString()}` : ""}`,
    signal ? { signal } : undefined
  );
}

export async function archiveArtifact(artifactId: string): Promise<ArtifactRecord> {
  const payload = await apiJson<{ artifact: ArtifactRecord }>(
    `/api/artifacts/${encodeURIComponent(artifactId)}`,
    { method: "DELETE" }
  );
  return payload.artifact;
}

export function listFiles(path = ""): Promise<FileBrowserDirectory> {
  return apiJson(fileApiUrl("/api/files", path));
}

export function inspectFile(path: string): Promise<FileBrowserEntry> {
  return apiJson(fileApiUrl("/api/files/info", path));
}

export function readTextFile(path: string): Promise<FileTextPreview> {
  return apiJson(fileApiUrl("/api/files/content", path));
}

export function rawFileUrl(path: string, download = false): string {
  return fileApiUrl("/api/files/raw", path, download);
}

export function getCapabilities(
  refresh = false,
  projectId: string | null = null
): Promise<CapabilityInventory> {
  const query = new URLSearchParams();
  if (refresh) query.set("refresh", "true");
  if (projectId) query.set("projectId", projectId);
  return apiJson(`/api/capabilities${query.size ? `?${query.toString()}` : ""}`);
}

export function updateCapabilityPolicy(
  capabilityId: string,
  input: {
    projectId: string | null;
    enabled: boolean | null;
    approvalMode: CapabilityApprovalMode | null;
  }
): Promise<{ revision: number }> {
  return apiJson(`/api/capability-policies/${encodeURIComponent(capabilityId)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...(input.projectId ? { projectId: input.projectId } : {}),
      availability: input.enabled == null ? "inherit" : input.enabled ? "enabled" : "disabled",
      approvalMode: input.approvalMode ?? "inherit",
    }),
  });
}

export function getMemories(filters: {
  query?: string;
  status?: MemoryStatus | "all";
  scope?: MemoryScope | "all";
  projectId?: string | null;
} = {}, signal?: AbortSignal): Promise<{ memories: MemoryRecord[]; total: number }> {
  const query = new URLSearchParams();
  if (filters.query) query.set("q", filters.query);
  if (filters.status) query.set("status", filters.status);
  if (filters.scope) query.set("scope", filters.scope);
  if (filters.projectId) query.set("projectId", filters.projectId);
  const suffix = query.size ? `?${query.toString()}` : "";
  return apiJson(`/api/memories${suffix}`, signal ? { signal } : undefined);
}

export async function createMemory(input: {
  title: string;
  content: string;
  kind: MemoryKind;
  scope: MemoryScope;
  projectId: string | null;
  sourceSessionId: string | null;
  confidence?: number;
  expiresAt?: string | null;
}): Promise<{ memory: MemoryRecord; created: boolean }> {
  return apiJson("/api/memories", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
}

export async function updateMemory(
  memoryId: string,
  input: Partial<{
    title: string;
    content: string;
    kind: MemoryKind;
    scope: MemoryScope;
    projectId: string | null;
    confidence: number;
    status: MemoryStatus;
    expiresAt: string | null;
  }>
): Promise<MemoryRecord> {
  const payload = await apiJson<{ memory: MemoryRecord }>(`/api/memories/${encodeURIComponent(memoryId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return payload.memory;
}

export async function forgetMemory(memoryId: string): Promise<MemoryRecord> {
  const payload = await apiJson<{ memory: MemoryRecord }>(`/api/memories/${encodeURIComponent(memoryId)}`, {
    method: "DELETE",
  });
  return payload.memory;
}

export async function restoreMemory(memoryId: string): Promise<MemoryRecord> {
  const payload = await apiJson<{ memory: MemoryRecord }>(`/api/memories/${encodeURIComponent(memoryId)}/restore`, {
    method: "POST",
  });
  return payload.memory;
}

export function getMemoryHygieneFindings(signal?: AbortSignal): Promise<{ findings: MemoryHygieneFinding[]; total: number }> {
  return apiJson("/api/memory-hygiene", signal ? { signal } : undefined);
}

export function scanMemoryHygiene(): Promise<{ scanned: number; findings: number; truncated: boolean; inProgress: boolean }> {
  return apiJson("/api/memory-hygiene/scan", { method: "POST" });
}

export async function dismissMemoryHygieneFinding(findingId: string): Promise<MemoryHygieneFinding> {
  const payload = await apiJson<{ finding: MemoryHygieneFinding }>(
    `/api/memory-hygiene/${encodeURIComponent(findingId)}/dismiss`,
    { method: "POST" }
  );
  return payload.finding;
}

export async function confirmMemoryHygieneFinding(findingId: string): Promise<MemoryHygieneFinding> {
  const payload = await apiJson<{ finding: MemoryHygieneFinding }>(
    `/api/memory-hygiene/${encodeURIComponent(findingId)}/confirm`,
    { method: "POST" }
  );
  return payload.finding;
}

export async function mergeMemoryHygieneFinding(
  findingId: string,
  keepMemoryId: string
): Promise<MemoryHygieneFinding> {
  const payload = await apiJson<{ finding: MemoryHygieneFinding }>(
    `/api/memory-hygiene/${encodeURIComponent(findingId)}/merge`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ keepMemoryId }),
    }
  );
  return payload.finding;
}

export async function getPersonality(signal?: AbortSignal): Promise<NoraPersonality> {
  const payload = await apiJson<{ personality: NoraPersonality }>(
    "/api/personality",
    signal ? { signal } : undefined
  );
  return payload.personality;
}

export async function updatePersonality(
  input: Omit<NoraPersonality, "updatedAt">
): Promise<NoraPersonality> {
  const payload = await apiJson<{ personality: NoraPersonality }>("/api/personality", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return payload.personality;
}

export async function createProject(input: {
  name: string;
  description: string;
  objective: string;
  successCriteria: string;
  nextAction: string;
  trackingMode: ProjectTrackingMode;
  workspacePath: string;
}): Promise<ProjectSummary> {
  const payload = await apiJson<{ project: ProjectSummary }>("/api/projects", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return payload.project;
}

export function getProjectDetail(projectId: string, signal?: AbortSignal): Promise<ProjectDetail> {
  return apiJson(
    `/api/projects/${encodeURIComponent(projectId)}`,
    signal ? { signal } : undefined
  );
}

export function getRecurringTask(taskId: string, signal?: AbortSignal): Promise<RecurringTaskDetail> {
  return apiJson(
    `/api/recurring-tasks/${encodeURIComponent(taskId)}`,
    signal ? { signal } : undefined
  );
}

export async function createRecurringTask(input: {
  name: string;
  description: string;
  goal: string;
  schedule: RecurringTaskSchedule;
  timezone: string;
  projectId: string | null;
  notifyOnCompletion: boolean;
  modelId: string | null;
  reasoningEffort: string | null;
  serviceTier: string | null;
  status: "active" | "paused";
}): Promise<RecurringTaskRecord> {
  const payload = await apiJson<{ task: RecurringTaskRecord }>("/api/recurring-tasks", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return payload.task;
}

export async function updateRecurringTask(
  taskId: string,
  input: Partial<{
    name: string;
    description: string;
    goal: string;
    schedule: RecurringTaskSchedule;
    timezone: string;
    notifyOnCompletion: boolean;
    modelId: string | null;
    reasoningEffort: string | null;
    serviceTier: string | null;
    status: "active" | "paused";
  }>
): Promise<RecurringTaskRecord> {
  const payload = await apiJson<{ task: RecurringTaskRecord }>(
    `/api/recurring-tasks/${encodeURIComponent(taskId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }
  );
  return payload.task;
}

export async function runRecurringTask(taskId: string): Promise<RecurringTaskRunRecord> {
  const payload = await apiJson<{ run: RecurringTaskRunRecord }>(
    `/api/recurring-tasks/${encodeURIComponent(taskId)}/run`,
    { method: "POST" }
  );
  return payload.run;
}

export async function archiveRecurringTask(taskId: string): Promise<RecurringTaskRecord> {
  const payload = await apiJson<{ task: RecurringTaskRecord }>(
    `/api/recurring-tasks/${encodeURIComponent(taskId)}`,
    { method: "DELETE" }
  );
  return payload.task;
}

export function updateProject(
  projectId: string,
  input: Partial<{
    name: string;
    description: string;
    objective: string;
    successCriteria: string;
    nextAction: string;
    trackingMode: ProjectTrackingMode;
    progressStatus: ProjectProgressStatus;
    autonomyMode: ProjectAutonomyMode;
    autonomyMaxDurationMinutes: number;
    autonomyMaxTokens: number;
    autonomyMaxToolSteps: number;
    autonomyMaxAttempts: number;
  }>
): Promise<ProjectDetail> {
  return apiJson(`/api/projects/${encodeURIComponent(projectId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function advanceProject(projectId: string): Promise<{ run: { id: string }; session: SessionSummary }> {
  return apiJson(`/api/projects/${encodeURIComponent(projectId)}/advance`, { method: "POST" });
}

export async function createProjectGoal(
  projectId: string,
  input: { title: string; successCriteria: string; status: ProjectGoalStatus; dueAt: string | null }
): Promise<ProjectGoalRecord> {
  const payload = await apiJson<{ goal: ProjectGoalRecord }>(`/api/projects/${encodeURIComponent(projectId)}/goals`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return payload.goal;
}

export async function updateProjectGoal(
  goalId: string,
  input: Partial<{ title: string; successCriteria: string; status: ProjectGoalStatus; dueAt: string | null }>
): Promise<ProjectGoalRecord> {
  const payload = await apiJson<{ goal: ProjectGoalRecord }>(`/api/project-goals/${encodeURIComponent(goalId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return payload.goal;
}

export async function cancelProjectGoal(goalId: string): Promise<ProjectGoalRecord> {
  const payload = await apiJson<{ goal: ProjectGoalRecord }>(`/api/project-goals/${encodeURIComponent(goalId)}`, {
    method: "DELETE",
  });
  return payload.goal;
}

export async function createProjectBlocker(
  projectId: string,
  input: { goalId: string | null; title: string; details: string }
): Promise<ProjectBlockerRecord> {
  const payload = await apiJson<{ blocker: ProjectBlockerRecord }>(`/api/projects/${encodeURIComponent(projectId)}/blockers`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return payload.blocker;
}

export async function updateProjectBlocker(
  blockerId: string,
  input: Partial<{ title: string; details: string; status: "open" | "resolved" }>
): Promise<ProjectBlockerRecord> {
  const payload = await apiJson<{ blocker: ProjectBlockerRecord }>(`/api/project-blockers/${encodeURIComponent(blockerId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return payload.blocker;
}

export function getConfiguration(refresh = false, signal?: AbortSignal): Promise<ConfigurationPayload> {
  return apiJson(
    `/api/configuration${refresh ? "?refresh=true" : ""}`,
    signal ? { signal } : undefined
  );
}

export async function updateSettings(
  input: Omit<NoraSettings, "updatedAt">
): Promise<NoraSettings> {
  const payload = await apiJson<{ settings: NoraSettings }>("/api/settings", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return payload.settings;
}

export async function updateTheme(themeId: ThemeId): Promise<NoraSettings> {
  const payload = await apiJson<{ settings: NoraSettings }>("/api/settings/theme", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ themeId }),
  });
  return payload.settings;
}

export async function updateSessionSettings(
  sessionId: string,
  input: AgentSelection
): Promise<SessionSummary> {
  const payload = await apiJson<{ session: SessionSummary }>(
    `/api/sessions/${encodeURIComponent(sessionId)}/settings`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    }
  );
  return payload.session;
}

export async function getSession(sessionId: string): Promise<SessionDetail> {
  const payload = await apiJson<SessionDetail>(`/api/sessions/${encodeURIComponent(sessionId)}`);
  if (Array.isArray(payload.approvals)) return payload;
  const approvals = await getPendingApprovals();
  return {
    ...payload,
    approvals: approvals.filter((approval) => approval.sessionId === sessionId),
  };
}

export async function getQuickPrompts(): Promise<QuickPromptRecord[]> {
  const payload = await apiJson<{ prompts: QuickPromptRecord[] }>("/api/quick-prompts");
  return payload.prompts;
}

export async function createQuickPrompt(input: {
  title: string;
  description: string;
  content: string;
  context: QuickPromptContext;
}): Promise<QuickPromptRecord> {
  const payload = await apiJson<{ prompt: QuickPromptRecord }>("/api/quick-prompts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return payload.prompt;
}

export async function updateQuickPrompt(
  promptId: string,
  input: Partial<{ title: string; description: string; content: string; context: QuickPromptContext }>
): Promise<QuickPromptRecord> {
  const payload = await apiJson<{ prompt: QuickPromptRecord }>(`/api/quick-prompts/${encodeURIComponent(promptId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return payload.prompt;
}

export function deleteQuickPrompt(promptId: string): Promise<Response> {
  return apiFetch(`/api/quick-prompts/${encodeURIComponent(promptId)}`, { method: "DELETE" });
}

export async function listPendingAttachments(sessionId: string): Promise<AttachmentRecord[]> {
  const payload = await apiJson<{ attachments: AttachmentRecord[] }>(
    `/api/sessions/${encodeURIComponent(sessionId)}/attachments`
  );
  return payload.attachments;
}

export function uploadAttachment(
  sessionId: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<AttachmentRecord> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("POST", `/api/sessions/${encodeURIComponent(sessionId)}/attachments`);
    request.responseType = "json";
    request.withCredentials = true;
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100));
    };
    request.onerror = () => reject(new ApiError("Envoi de la pièce jointe impossible.", 0));
    request.onload = () => {
      const payload = request.response && typeof request.response === "object" ? request.response : {};
      if (request.status >= 200 && request.status < 300 && payload.attachment) {
        resolve(payload.attachment as AttachmentRecord);
        return;
      }
      reject(new ApiError(String(payload.error || `Erreur HTTP ${request.status}`), request.status));
    };
    const body = new FormData();
    body.append("file", file, file.name);
    request.send(body);
  });
}

export function deleteAttachment(attachmentId: string): Promise<Response> {
  return apiFetch(`/api/attachments/${encodeURIComponent(attachmentId)}`, { method: "DELETE" });
}

export function attachmentRawUrl(attachmentId: string): string {
  return `/api/attachments/${encodeURIComponent(attachmentId)}/raw`;
}

export async function createSession(input: {
  title: string;
  projectId: string | null;
  workspacePath: string;
  driverId: DriverId;
}): Promise<SessionSummary> {
  const payload = await apiJson<{ session: SessionSummary }>("/api/sessions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return payload.session;
}

export function deleteSession(sessionId: string): Promise<Response> {
  return apiFetch(`/api/sessions/${encodeURIComponent(sessionId)}`, { method: "DELETE" });
}

export function sendMessage(sessionId: string, content: string, attachmentIds: string[] = []): Promise<unknown> {
  return apiJson(`/api/sessions/${encodeURIComponent(sessionId)}/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content, attachmentIds }),
  });
}

export function sendInteractionResponse(
  sessionId: string,
  requestId: string,
  answers: InteractionAnswerInput[]
): Promise<unknown> {
  return apiJson(`/api/sessions/${encodeURIComponent(sessionId)}/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ interactionResponse: { requestId, answers } }),
  });
}

export function retryMessage(messageId: string): Promise<unknown> {
  return apiJson(`/api/messages/${encodeURIComponent(messageId)}/retry`, { method: "POST" });
}

export function cancelRun(runId: string): Promise<unknown> {
  return apiJson(`/api/runs/${encodeURIComponent(runId)}/cancel`, { method: "POST" });
}

async function apiJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await apiFetch(url, init);
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

async function apiFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const response = await fetch(url, { ...init, credentials: "same-origin" });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new ApiError(String(payload.error || `Erreur HTTP ${response.status}`), response.status);
  }
  return response;
}

function fileApiUrl(endpoint: string, path: string, download = false): string {
  const query = new URLSearchParams();
  if (path) query.set("path", path);
  if (download) query.set("download", "1");
  const suffix = query.toString();
  return suffix ? `${endpoint}?${suffix}` : endpoint;
}
